import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { competitorNameFromDomain } from '@/api/aiVisibility';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { useAppForm } from '@/components/Form';
import { FieldLabel } from '@/components/Form/fields';

type AIVisibilityCompetitorNameDialogProps = {
  domain: string;
  onCancel: () => void;
  onConfirm: (name: string) => void;
};

const AIVisibilityCompetitorNameDialog = ({
  domain,
  onCancel,
  onConfirm,
}: AIVisibilityCompetitorNameDialogProps) => {
  const formSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t`Enter a competitor name.`),
  });

  const form = useAppForm({
    defaultValues: {
      name: competitorNameFromDomain(domain),
    },
    validators: {
      onBlur: formSchema,
      onSubmit: formSchema,
    },
    onSubmit: ({ value }) => {
      onConfirm(value.name);
    },
  });

  return (
    <Dialog
      open
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Trans>Competitor name</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>
              What is the name of the company with the domain: <strong>{domain}</strong>?
            </Trans>
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.AppForm>
            <form.AppField name="name">
              {(field) => (
                <field.Field>
                  <FieldLabel htmlFor="ai-visibility-competitor-name" className="sr-only">
                    <Trans>Competitor name</Trans>
                  </FieldLabel>
                  <field.TextInput
                    id="ai-visibility-competitor-name"
                    size="large"
                    placeholder={t`Enter competitor name...`}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault();
                        form.handleSubmit();
                      }
                    }}
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
            <DialogFooter>
              <DialogClose
                render={
                  <Button type="button" size="large">
                    <Trans>Cancel</Trans>
                  </Button>
                }
              />
              <form.SubmitButton size="large" variant="on-primary">
                <Trans>OK</Trans>
              </form.SubmitButton>
            </DialogFooter>
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AIVisibilityCompetitorNameDialog;
